import { APP_CONFIG } from '../config/app-config';

export type KycStep = 'identity' | 'address';

export type KycDocumentType = 'id_card' | 'utility_bill' | 'id_document' | 'proof_of_address';

export interface KycUserFeedback {
  title?: string;
  message?: string;
  issues?: string[];
  tips?: string[];
  documentType?: string;
}

export interface KycRecordSnapshot {
  status?: string;
  documentType?: string;
  rejectionReason?: string | null;
  userFeedback?: KycUserFeedback | null;
}

export interface KycStatusSnapshot {
  kycStatus: string;
  kycTier: number | null;
  kycRecord: KycRecordSnapshot | null;
  kycRecords?: KycRecordSnapshot[];
}

export const ID_FILENAME_HINTS = [
  'id',
  'passport',
  'licence',
  'license',
  'drivers',
  'driver',
  'identity',
  'sa_id',
  'smartcard',
];

export const POA_FILENAME_HINTS = [
  'bill',
  'statement',
  'utility',
  'eskom',
  'municipal',
  'water',
  'rates',
  'account',
  'invoice',
  'proof_of_address',
  'poa',
  'bank',
  'telco',
  'vodacom',
  'mtn',
  'telkom',
  'rain',
  'cellc',
  'fibre',
  'afrihost',
  'lease',
  'rental',
  'tenancy',
  'sars',
  'tax',
  'payslip',
  'salary',
  'levy',
  'store',
  'woolworths',
  'truworths',
  'tfg',
  'edgars',
];

const POA_REJECTION_PATTERNS = /days old|older than|90 days|12 months|utility|address|statement|municipal|invoice date|lease|rental|po box|private bag|telco|store account|sars|residential/i;

export function deriveIdentityApproved(tier: number | null | undefined): boolean {
  return (tier ?? 0) >= 1;
}

export function deriveShowPoaStep(tier: number | null | undefined): boolean {
  return deriveIdentityApproved(tier) && (tier ?? 0) < 2;
}

export function deriveFailedDocumentLabel(opts: {
  tier?: number | null;
  kycStatus?: string;
  documentType?: string | null;
  rejectionReason?: string | null;
  userFeedback?: KycUserFeedback | null;
}): 'Identity document' | 'Proof of address' {
  const feedbackDocType = opts.userFeedback?.documentType;
  if (feedbackDocType === 'proof_of_address' || feedbackDocType === 'utility_bill') {
    return 'Proof of address';
  }
  if (feedbackDocType === 'id_document' || feedbackDocType === 'id_card') {
    return 'Identity document';
  }

  const docType = opts.documentType;
  if (docType === 'utility_bill' || docType === 'proof_of_address') {
    return 'Proof of address';
  }
  if ((docType === 'id_card' || docType === 'id_document') && !deriveIdentityApproved(opts.tier)) {
    return 'Identity document';
  }

  if (deriveIdentityApproved(opts.tier) && opts.kycStatus === 'rejected') {
    return 'Proof of address';
  }

  const reason = opts.rejectionReason || opts.userFeedback?.message || '';
  if (POA_REJECTION_PATTERNS.test(reason)) {
    return 'Proof of address';
  }

  return 'Identity document';
}

export function buildRejectionTitle(label: 'Identity document' | 'Proof of address', serverTitle?: string): string {
  if (serverTitle && !/couldn't verify your document/i.test(serverTitle)) {
    return serverTitle;
  }
  return `${label} verification failed`;
}

export function looksLikeWrongDocumentForStep(step: KycStep, filename: string): string | null {
  const lower = filename.toLowerCase();
  if (step === 'identity' && POA_FILENAME_HINTS.some((hint) => lower.includes(hint))) {
    return 'This looks like a bill or statement. Step 1 requires your SA ID book, SA ID card, or passport.';
  }
  if (step === 'address' && ID_FILENAME_HINTS.some((hint) => lower.includes(hint))) {
    return 'This looks like an identity document. Step 2 requires a utility bill, bank statement, telco bill, store account, or lease agreement.';
  }
  return null;
}

export async function fetchKycStatusSnapshot(token: string): Promise<KycStatusSnapshot | null> {
  const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/kyc/status`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  return {
    kycStatus: data.kycStatus || 'not_started',
    kycTier: data.kycTier != null ? Number(data.kycTier) : null,
    kycRecord: data.kycRecord || null,
    kycRecords: Array.isArray(data.kycRecords) ? data.kycRecords : (data.kycRecord ? [data.kycRecord] : []),
  };
}

/**
 * Find a rejected proof-of-address row in the per-document `kycRecords` array
 * (returned by GET /api/v1/kyc/status since 2026-08-18). Returns the rejected
 * POA record (with its userFeedback) when one exists, else null.
 *
 * Used to surface a POA-rejection notice to a Tier-1-verified user whose POA
 * failed — without demoting their overall kycStatus (which stays 'verified').
 * The user is Tier 1 verified (identity approved) but their POA (Tier 2) was
 * rejected, so they should be told to re-upload a proof of address.
 */
export function findRejectedPoaRecord(kycRecords?: KycRecordSnapshot[] | null): KycRecordSnapshot | null {
  if (!Array.isArray(kycRecords) || kycRecords.length === 0) return null;
  return kycRecords.find((r) =>
    r && r.status === 'rejected' &&
    (r.documentType === 'utility_bill' || r.documentType === 'bank_statement' ||
     r.userFeedback?.documentType === 'proof_of_address')
  ) || null;
}

export function buildRejectionFeedbackFromSnapshot(snapshot: KycStatusSnapshot): {
  title: string;
  issues: string[];
  tips: string[];
  failedDocumentLabel: 'Identity document' | 'Proof of address';
} | null {
  if (snapshot.kycStatus !== 'rejected' || !snapshot.kycRecord) {
    return null;
  }

  const { kycRecord } = snapshot;
  const feedback = kycRecord.userFeedback;
  const failedDocumentLabel = deriveFailedDocumentLabel({
    tier: snapshot.kycTier,
    kycStatus: snapshot.kycStatus,
    documentType: kycRecord.documentType,
    rejectionReason: kycRecord.rejectionReason,
    userFeedback: feedback,
  });

  const issues = [
    ...(feedback?.issues || []),
    ...(feedback?.message && !(feedback.issues || []).includes(feedback.message) ? [feedback.message] : []),
    ...(kycRecord.rejectionReason &&
    kycRecord.rejectionReason !== feedback?.message &&
    !(feedback?.issues || []).includes(kycRecord.rejectionReason)
      ? [kycRecord.rejectionReason]
      : []),
  ].filter((value, index, arr) => value && arr.indexOf(value) === index) as string[];

  const tips = feedback?.tips?.length
    ? feedback.tips
    : failedDocumentLabel === 'Proof of address'
      ? [
          'Upload a clear photo of a utility bill, bank statement, or municipal account',
          'The document must be less than 3 months old',
          'Your name and address must be clearly visible',
          'JPEG, PNG, or PDF supported, max 10MB',
        ]
      : [
          'Upload a clear photo of your SA ID book, SA ID card, or passport',
          'Ensure the whole document is visible with good lighting (no glare)',
          'JPEG, PNG, or PDF supported, max 10MB',
        ];

  return {
    title: buildRejectionTitle(failedDocumentLabel, feedback?.title),
    issues,
    tips,
    failedDocumentLabel,
  };
}
